import { NetworkStatus, useApolloClient, useQuery as useApolloQuery, useMutation } from '@apollo/client';
import { useFeatureFlag, useIsMobile } from '@newfront-insurance/admin-ui';
import { graphql } from '@newfront-insurance/graphql-frontend';
import type { AuthProviderContext } from '@newfront-insurance/next-auth';
import { useNotify } from '@newfront-insurance/next-notifications';
import { useQuery } from '@newfront-insurance/next-router-provider';
import type { SelectedAccount } from '@newfront-insurance/nova-core';
import { useProvider, type Provider } from '@newfront-insurance/react-provision';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { v4 } from 'uuid';

import { useMarkChatAsRead } from '../../../hooks/use-mark-chat-as-read';
import { useNovaSettings } from '../../../hooks/use-nova-settings';
import type { MessageReaction } from '../../../lib/types';
import { getChatPlaceholder } from '../../../lib/utils';
import type { ChatAppType } from '../../../queries/chats';
import { GET_ACCOUNT, GET_CHAT_WITH_MESSAGES, GET_CHATS_BY_FILTERS } from '../../../queries/chats';
import { msGraphManager } from '../../../services/ms-graph-manager';
import { ChatOverlayProvider } from '../contexts/chat-overlay-context';
import { useChatNotificationController } from '../hooks/use-chat-notification-controller';
import { useNotificationClickNavigation } from '../hooks/use-notification-click-navigation';
import { useOutlookConnectController } from '../hooks/use-outlook-connect-controller';
import { resolveEffectiveAccountUuid, resolvePageAccountUuid } from '../lib/account-bootstrap';
import { backendMessageToSDKMessage } from '../lib/chat-utils';
import { normalizeChatUuid } from '../lib/notification-helpers';
import type { ActiveChat, ChatError } from '../lib/shared-types';

const SET_CHAT_ACCOUNT_MUTATION = graphql(`
  mutation SetChatAccount($chatUuid: String!, $accountUuid: String!) {
    setChatAccount(chatUuid: $chatUuid, accountUuid: $accountUuid) {
      uuid
      account {
        uuid
        name
      }
    }
  }
`);

// Context for wrapper components to access Nova state
interface NovaWrapperState {
  isChatVisible: boolean;
  isFloatingButtonVisible: boolean;
  hasNotifications: boolean;
  handleToggle: () => void;
  handleOpen: () => void;
  handleClose: () => void;
}

const NovaWrapperContext = createContext<NovaWrapperState | undefined>(undefined);

export function useNovaWrapperState(): NovaWrapperState {
  const context = useContext(NovaWrapperContext);
  if (!context) {
    throw new Error('useNovaWrapperState must be used within NovaProvider');
  }
  return context;
}

export interface NovaProviderCallbacks {
  /** Called when starting a new chat */
  onStartNewChat?: (accountContext?: { uuid: string; name: string }) => void;
  /** Called when an account is selected */
  onAccountSelect?: (account: SelectedAccount) => void;
  /** Called when chat finishes */
  onFinish?: () => void;
  /** Called when chat errors */
  onError?: (error: Error) => void;
  /** Called when chat receives data */
  onData?: (dataPart: { type: string; data: unknown }) => void;
  /** Called when the active chat changes (e.g., from sidebar selection) */
  onChatChange?: (chatUuid: string) => void;
  /** Called when a user reacts to a message (thumbs up/down) */
  onReaction?: (reaction: MessageReaction | null, messageId: string) => void;
}

export interface NovaProviderProps {
  children: React.ReactNode;
  userUuid: string;
  scopes?: string[];
  authProvider: Provider<AuthProviderContext>;
  isStandaloneMode: boolean;
  isSidebarHidden?: boolean;
  initialAccountUuid?: string;
  initialChatUuid?: string;
  /** App origin for filtering chats (e.g. OUTLOOK) */
  appType?: ChatAppType;
  /** Called when chat visibility changes */
  onVisibilityChange?: (isVisible: boolean) => void;
  /** Called when chat should close (for custom close behavior) */
  onClose?: () => void;
  /** Called when chat overlay becomes visible - useful for refetching data and marking as read */
  onChatOverlayVisible?: (chatUuid: string) => void;
  callbacks?: NovaProviderCallbacks;
  /** Custom error message shown inline when a stream request fails */
  errorMessage?: string;
  /** Disables all write interactions and keeps the view read only */
  isReadOnly?: boolean;
}

/**
 * Provider that encapsulates all chat overlay logic including:
 * - State management (visibility, activeChat, accountUuid, notifications, errors, sidebar, fullscreen)
 * - GraphQL queries (chat messages, account data)
 * - Event handlers (keyboard shortcuts, account selection, new chat)
 * - Context value creation
 *
 * Exposes callbacks for customization while handling all internal logic.
 */
export function NovaProvider({
  children,
  userUuid,
  scopes,
  authProvider,
  isStandaloneMode,
  isSidebarHidden,
  initialAccountUuid,
  initialChatUuid,
  onVisibilityChange,
  onClose,
  onChatOverlayVisible,
  callbacks,
  appType,
  errorMessage,
  isReadOnly = false,
}: NovaProviderProps) {
  const { isMobile } = useIsMobile();
  const { authState } = useProvider(authProvider);
  const isPublicMode = !authState.isLoggedIn;
  const queryAccountUuid = useQuery<string | undefined>('accountUuid');

  // MS Graph API integration
  const msGraphEnabled = useFeatureFlag('nova-tools-graph-api', false);
  useEffect(() => {
    msGraphManager.setFeatureFlagEnabled(msGraphEnabled);
  }, [msGraphEnabled]);
  const showAllNovaChats = useFeatureFlag('show-all-nova-chats', false);
  const effectiveAppType = showAllNovaChats ? null : (appType ?? undefined);

  // Internal state management
  const [isChatVisible, setIsChatVisible] = useState(isStandaloneMode ?? false);
  const [isFloatingButtonVisible] = useState(true);
  const [activeChat, setActiveChat] = useState<ActiveChat | undefined>(
    initialChatUuid ? { chatUuid: initialChatUuid, hasAnyMessages: false } : undefined,
  );
  const [activeAccountUuid, setActiveAccountUuid] = useState<string | undefined>(undefined);
  const [chatError, setChatError] = useState<ChatError | undefined>(undefined);
  const [additionalContext, setAdditionalContext] = useState<Record<string, unknown>>({});

  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile && !isSidebarHidden && !!isStandaloneMode);
  const [isFullscreen, setIsFullscreen] = useState(isStandaloneMode ?? false);
  const [selectedAccount, setSelectedAccount] = useState<SelectedAccount>(undefined);
  const [hasAnyMessages, setHasAnyMessages] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const notify = useNotify();

  const pageAccountUuid = resolvePageAccountUuid({ initialAccountUuid, queryAccountUuid });

  const chatUuid = useMemo(() => {
    return activeChat?.chatUuid || initialChatUuid || `new-chat-${v4()}`;
  }, [activeChat?.chatUuid, initialChatUuid]);

  const { serverSettings, persistSetting, persistSettings } = useNovaSettings(userUuid);

  const notificationController = useChatNotificationController({
    userUuid,
    currentChatUuid: chatUuid,
    effectiveAppType,
    serverSettings,
    onPersist: persistSetting,
  });

  const { hasNotifications, setHasNotifications } = notificationController;

  // Hook to mark chats as read when opened
  const markChatAsRead = useMarkChatAsRead(userUuid, effectiveAppType);

  const isNewChat = !chatUuid || chatUuid.startsWith('new-chat-');

  // Handle visibility changes
  useEffect(() => {
    onVisibilityChange?.(isChatVisible);
  }, [isChatVisible, onVisibilityChange]);
  const { data, networkStatus } = useApolloQuery(GET_CHAT_WITH_MESSAGES, {
    variables: { uuid: chatUuid },
    skip: isPublicMode || !chatUuid || isNewChat,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  const apolloClient = useApolloClient();
  const loading = networkStatus === NetworkStatus.loading || (networkStatus === NetworkStatus.setVariables && !data);
  const chatWithMessages = data?.getChat;
  const currentChatAppType = chatWithMessages?.appType ?? undefined;
  const persistedChatAccountUuid = chatWithMessages?.account?.uuid ?? chatWithMessages?.accountUuid ?? undefined;
  const effectiveAccountUuid =
    persistedChatAccountUuid ??
    resolveEffectiveAccountUuid({
      activeAccountUuid,
      initialAccountUuid,
      queryAccountUuid,
    });

  useEffect(() => {
    if (!initialChatUuid || activeChat?.chatUuid === initialChatUuid) {
      return;
    }

    setActiveChat({
      chatUuid: initialChatUuid,
      hasAnyMessages: false,
    });
    setActiveAccountUuid(undefined);
    setSelectedAccount(undefined);
    setHasAnyMessages(false);
  }, [activeChat?.chatUuid, initialChatUuid]);

  useEffect(() => {
    if (chatWithMessages?.messages) {
      setHasAnyMessages(chatWithMessages.messages.length > 0);
    }
  }, [chatWithMessages?.messages]);

  // Handle chat overlay becoming visible - mark chat as read if it has unread messages
  const handleChatOverlayVisible = useCallback(() => {
    if (!chatUuid) {
      return;
    }

    if (!isReadOnly) {
      const chats = apolloClient.readQuery({
        query: GET_CHATS_BY_FILTERS,
        variables: { userUuid, appType: effectiveAppType },
      });
      const chat = chats?.chatsList?.find(
        (chatItem) => normalizeChatUuid(chatItem.uuid ?? '') === normalizeChatUuid(chatUuid),
      );
      // If the current chat has unread messages, mark it as read
      // The chat data is already available (optimistically added to sidebar)
      if (chat?.hasUnreadMessages) {
        markChatAsRead(normalizeChatUuid(chatUuid));
      }
    }

    // Call the external callback if provided
    onChatOverlayVisible?.(chatUuid);
  }, [chatUuid, markChatAsRead, onChatOverlayVisible, apolloClient, userUuid, effectiveAppType, isReadOnly]);

  useEffect(() => {
    if (isChatVisible && chatUuid) {
      handleChatOverlayVisible();
    }
  }, [isChatVisible, isNewChat, chatUuid, handleChatOverlayVisible]);

  const { data: accountData } = useApolloQuery(GET_ACCOUNT, {
    variables: { uuid: effectiveAccountUuid as string },
    skip: isPublicMode || !effectiveAccountUuid,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const shouldFetchCurrentAccount = isNewChat && !effectiveAccountUuid;
  const { data: currentAccountData } = useApolloQuery(GET_ACCOUNT, {
    variables: { uuid: effectiveAccountUuid as string },
    skip: isPublicMode || !shouldFetchCurrentAccount || !effectiveAccountUuid,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const [setChatAccountMutation] = useMutation(SET_CHAT_ACCOUNT_MUTATION);

  const initialMessages = useMemo(
    () => chatWithMessages?.messages.map(backendMessageToSDKMessage) ?? [],
    [chatWithMessages?.messages],
  );

  // Determine which account to display (priority: chat data > stored context > current page context)
  const displayAccount = useMemo(() => {
    const account =
      chatWithMessages?.account ??
      (effectiveAccountUuid && accountData?.getAccount
        ? { uuid: accountData.getAccount.uuid, name: accountData.getAccount.name }
        : null) ??
      currentAccountData?.getAccount ??
      null;

    return account?.uuid ? { uuid: account.uuid, name: account.name } : undefined;
  }, [chatWithMessages?.account, effectiveAccountUuid, accountData?.getAccount, currentAccountData?.getAccount]);

  // Compute placeholder based on account context
  const placeholder = useMemo(() => {
    const accountNameToUse = displayAccount?.name ?? accountData?.getAccount?.name;
    return getChatPlaceholder(accountNameToUse, accountData?.getAccount?.programType);
  }, [displayAccount?.name, accountData?.getAccount?.name, accountData?.getAccount?.programType]);

  // Handle close
  const handleClose = useCallback(() => {
    setIsChatVisible(false);
    onClose?.();
    callbacks?.onFinish?.();
  }, [onClose, callbacks]);

  // Ref to the controller's reconnectIfNeeded — allows handleOpen (defined before the
  // controller) to trigger interactive Outlook re-auth from the user's click gesture.
  const reconnectIfNeededRef = useRef<() => Promise<boolean>>(async () => false);

  // Handle open
  const handleOpen = useCallback(() => {
    setIsChatVisible(true);
    // Trigger Outlook re-auth popup from this click gesture if session expired.
    // The ref is updated after the controller initializes (below).
    reconnectIfNeededRef.current();
  }, []);

  // Handle toggle
  const handleToggle = useCallback(() => {
    if (isChatVisible) {
      handleClose();
    } else {
      handleOpen();
    }
  }, [isChatVisible, handleClose, handleOpen]);

  // Keyboard shortcuts handler
  useEffect(() => {
    if (!isChatVisible) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isSettingsOpen) {
          return setIsSettingsOpen(false);
        }
        if (isSidebarOpen) {
          return setIsSidebarOpen(false);
        }
        if (isStandaloneMode) {
          return;
        }
        if (isFullscreen) {
          setIsFullscreen(false);
        } else {
          handleClose();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleClose, isFullscreen, isStandaloneMode, isSidebarOpen, isChatVisible, isSettingsOpen]);

  useEffect(() => {
    if (!isNewChat) {
      setSelectedAccount(undefined);
    }
  }, [isNewChat]);

  const handleStartNewChat = useCallback(() => {
    if (isReadOnly) {
      return;
    }

    const accountContext = accountData?.getAccount
      ? {
          uuid: accountData.getAccount.uuid,
          name: accountData.getAccount.name,
        }
      : undefined;

    // Reset account UUID when starting a new chat so it falls back to the page-scoped account context.
    setActiveAccountUuid(undefined);
    setSelectedAccount(undefined);
    setHasAnyMessages(false); // Reset message count for new chat

    // Generate a new chat UUID directly - this ensures a fresh chat every time
    const newChatUuid = `new-chat-${v4()}`;

    setActiveChat({
      chatUuid: newChatUuid,
      accountContext: isStandaloneMode ? undefined : accountContext,
      hasAnyMessages: false,
    });

    callbacks?.onStartNewChat?.(
      accountContext
        ? {
            uuid: accountContext.uuid ?? '',
            name: accountContext.name ?? '',
          }
        : undefined,
    );
  }, [accountData?.getAccount, isStandaloneMode, callbacks, isReadOnly]);

  const handleAccountSelect = useCallback(
    (account: SelectedAccount) => {
      if (isReadOnly) {
        return;
      }

      setSelectedAccount(account);
      const accountUuidToSet = account?.uuid ?? undefined;
      setActiveAccountUuid(accountUuidToSet);

      setActiveChat({
        chatUuid,
        accountContext: account
          ? {
              uuid: account.uuid ?? null,
              name: account.label,
            }
          : { uuid: null },
        hasAnyMessages,
      });

      // If chat has messages, also update the account on the backend
      // This allows setting the account after chat creation, matching the old behavior
      if (account?.uuid && hasAnyMessages) {
        setChatAccountMutation({
          variables: {
            chatUuid: chatUuid.replace('new-chat-', ''),
            accountUuid: account.uuid,
          },
          optimisticResponse: {
            setChatAccount: {
              uuid: chatUuid,
              account: {
                uuid: account.uuid,
                name: account.label,
              },
            },
          },
        });
      }

      callbacks?.onAccountSelect?.(account);
    },
    [chatUuid, hasAnyMessages, setChatAccountMutation, callbacks, isReadOnly],
  );

  const handleSetActiveChat = useCallback(
    (chatUuidToSet: string, chatAccountUuid?: string | null) => {
      setActiveChat({
        chatUuid: chatUuidToSet,
        hasAnyMessages: true,
      });
      setActiveAccountUuid(chatAccountUuid ?? undefined);
      setSelectedAccount(undefined);
      setHasAnyMessages(false);
      callbacks?.onChatChange?.(chatUuidToSet);
    },
    [callbacks],
  );

  // Listen for notification clicks to navigate to the chat
  useNotificationClickNavigation({
    onSelectChat: handleSetActiveChat,
    onOpenChat: () => {
      if (!isChatVisible) {
        setIsChatVisible(true);
      }
    },
  });

  const overlayContextBase = useMemo(
    () => ({
      isSidebarOpen,
      setIsSidebarOpen,
      isFullscreen,
      setIsFullscreen,
      accountUuid: effectiveAccountUuid,
      accountName: accountData?.getAccount?.name,
      accountProgramType: accountData?.getAccount?.programType,
      selectedAccount,
      setAccountUuid: setActiveAccountUuid,
      onSelectAccount: handleAccountSelect,
      chatUuid,
      isNewChat,
      isReadOnly,
      hasAnyMessages,
      setHasAnyMessages,
      startNewChat: handleStartNewChat,
      setActiveChat: handleSetActiveChat,
      isStandaloneMode,
      isVisible: isChatVisible,
      onClose: handleClose,
      userUuid,
      userScopes: scopes,
      authProvider,
      displayAccount: displayAccount ?? undefined,
      placeholder,
      initialMessages,
      isLoadingMessages: loading,
      chatError,
      setChatError,
      hasNotifications,
      setHasNotifications,
      additionalContext,
      setAdditionalContext,
      queryAccountUuid,
      pageAccountUuid,
      callbacks,
      notificationController,
      appType,
      effectiveAppType,
      currentChatAppType,
      isPublicMode,
      isSettingsOpen,
      setIsSettingsOpen,
      errorMessage,
    }),
    [
      isSidebarOpen,
      isFullscreen,
      effectiveAccountUuid,
      accountData?.getAccount?.name,
      accountData?.getAccount?.programType,
      selectedAccount,
      chatUuid,
      isNewChat,
      isReadOnly,
      handleStartNewChat,
      handleAccountSelect,
      handleSetActiveChat,
      hasAnyMessages,
      setHasAnyMessages,
      setHasNotifications,
      isStandaloneMode,
      isChatVisible,
      handleClose,
      userUuid,
      scopes,
      authProvider,
      displayAccount,
      placeholder,
      initialMessages,
      loading,
      chatError,
      hasNotifications,
      additionalContext,
      queryAccountUuid,
      pageAccountUuid,
      callbacks,
      notificationController,
      appType,
      effectiveAppType,
      currentChatAppType,
      isPublicMode,
      isSettingsOpen,
      setIsSettingsOpen,
      errorMessage,
    ],
  );

  // Expose wrapper state for FloatingButton and other external components
  const wrapperState = useMemo<NovaWrapperState>(
    () => ({
      isChatVisible,
      isFloatingButtonVisible,
      hasNotifications,
      handleToggle,
      handleOpen,
      handleClose,
    }),
    [isChatVisible, isFloatingButtonVisible, hasNotifications, handleToggle, handleOpen, handleClose],
  );

  const { outlookConnectEnabled, outlookNeedsReauth, setOutlookConnectEnabled, reconnectIfNeeded } =
    useOutlookConnectController({
      msGraphEnabled,
      isChatVisible,
      notify,
      serverSettings,
      onPersist: persistSetting,
    });
  reconnectIfNeededRef.current = reconnectIfNeeded;

  const overlayContextValue = useMemo(
    () => ({
      ...overlayContextBase,
      outlookConnectEnabled,
      outlookNeedsReauth,
      reconnectOutlook: reconnectIfNeeded,
      setOutlookConnectEnabled,
      serverSettings,
      persistSetting,
      persistSettings,
    }),
    [
      overlayContextBase,
      outlookConnectEnabled,
      outlookNeedsReauth,
      reconnectIfNeeded,
      setOutlookConnectEnabled,
      serverSettings,
      persistSetting,
      persistSettings,
    ],
  );

  return (
    <NovaWrapperContext.Provider value={wrapperState}>
      <ChatOverlayProvider value={overlayContextValue}>{children}</ChatOverlayProvider>
    </NovaWrapperContext.Provider>
  );
}
